import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { handleApiError } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import MixtapeLogo from './ui/MixtapeLogo';
import Button from './ui/Button';

// Routes the modal must not cover, so users can read the documents it links to.
const EXEMPT_PATHS = ['/terms', '/privacy'];

const TITLE_ID = 'retro-consent-title';

// Shown once for accounts created before the registration consent checkbox
// was introduced (termsAcceptedAt is null). Blocks interaction with the app
// until the user accepts the Terms of Service and Privacy Policy.
const RetroactiveConsentModal = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const panelRef = useRef(null);

  const show =
    !!user && !user.isGuest && !user.termsAcceptedAt && !EXEMPT_PATHS.includes(location.pathname);

  // Dialog behaviour: lock body scroll, move focus into the panel, trap Tab
  // within it, and restore focus on close. No Escape handler — this is a
  // required consent gate, so it cannot be dismissed without accepting.
  useEffect(() => {
    if (!show) return undefined;
    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus();
    };
  }, [show]);

  if (!show) return null;

  const handleAccept = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/auth/consent/accept');
      updateUser(data.user);
      setLoading(false);
    } catch (error) {
      toast.error(handleApiError(error, 'Could not record your acceptance. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div ref={panelRef} tabIndex={-1} className="relative w-full max-w-md card p-6 sm:p-8 focus:outline-none">
        <div className="flex items-center gap-2.5 mb-4">
          <MixtapeLogo size={32} />
        </div>
        <h2 id={TITLE_ID} className="text-lg font-semibold text-brand-ink mb-2">Updated Terms &amp; Privacy Policy</h2>
        <p className="text-body text-secondary leading-relaxed mb-6">
          We've updated our{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Privacy Policy</Link>.
          To keep using Mixtape Memories, please confirm you've read and agree to them. This includes how we collect and process your personal information, including memorial photos as sensitive information, to provide this service.
        </p>
        <Button type="button" onClick={handleAccept} disabled={loading} className="w-full">
          {loading ? 'Saving…' : 'I Agree'}
        </Button>
      </div>
    </div>
  );
};

export default RetroactiveConsentModal;
